import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Modal,
  ModalFooter,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import DataTable from "react-data-table-component";
import ReactDatePicker from "react-datepicker";
import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { Filter, MoreVertical } from "react-feather";
import { holidayData } from "../../../data/holidays/CountryList";
import NavigationBreadcrumbs from "../../../common-elements/breadcrumbs/NavigationBreadcrumbs";
import CommonDropDown from "../../common/CommonDropDown";
import { tableHeaderStyles } from "../../../styles/ReactSelectStyles";
import { H4 } from "../../../AbstractElements";

const HolidaysTableList = () => {
  const { t } = useTranslation();
  const { countryName, year } = useParams<{
    countryName: string;
    year: string;
  }>();
  const [filterText, setFilterText] = useState("");
  const [filterToggle, setFilterToggle] = useState(!!(countryName && year));
  const [filters, setFilters] = useState({
    country: countryName || "",
    year: year || "",
    date: null as Date | null,
    type: "",
    status: "",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState<{
    id: number;
    name: string;
  } | null>(null);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      country: countryName || "",
      year: year || "",
    }));
  }, [countryName, year]);

  const toggleFilterSection = () => {
    setFilterToggle((prev) => !prev);
  };

  const toggleModal = () => {
    setModalOpen((prev) => !prev);
  };

  const countries = useMemo(() => {
    const uniqueCountries = Array.from(
      new Set(holidayData.map((item) => item.country))
    );
    const paramsCountry = countryName ? [countryName] : [];
    const allCountries = Array.from(
      new Set([...paramsCountry, ...uniqueCountries])
    );
    return ["", ...allCountries].map((country) => ({
      value: country,
      label: country ? t(country) : t("All"),
    }));
  }, [t, countryName]);

  const years = useMemo(() => {
    const uniqueYears = Array.from(
      new Set(holidayData.map((item) => item.date.split("/")[2]))
    );
    const paramsYear = year ? [year] : [];
    const additionalYears = ["2024", "2026", "2027"];
    const allYears = Array.from(
      new Set([...paramsYear, ...uniqueYears, ...additionalYears])
    ).sort();
    return ["", ...allYears].map((yr) => ({
      value: yr,
      label: yr ? yr : t("All"),
    }));
  }, [t, year]);

  const dates = useMemo(() => {
    const uniqueDates = Array.from(
      new Set(holidayData.map((item) => item.date))
    )
      .map((dateStr) => {
        const [month, day, year] = dateStr.split("/");
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      })
      .sort((a, b) => a.getTime() - b.getTime());
    return [null, ...uniqueDates].map((date) => ({
      value: date,
      label: date
        ? date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        : t("All"),
    }));
  }, [t]);

  const types = useMemo(() => {
    const uniqueTypes = Array.from(
      new Set(holidayData.map((item) => item.type))
    );
    return ["", ...uniqueTypes].map((type) => ({
      value: type,
      label: type ? t(type) : t("All"),
    }));
  }, [t]);

  const statuses = useMemo(() => {
    const uniqueStatuses = ["", "Active", "Inactive"];
    return uniqueStatuses.map((status) => ({
      value: status,
      label: status ? t(status) : t("All"),
    }));
  }, [t]);

  const handleFilterChange = (
    key: keyof typeof filters,
    value: string | Date | null
  ) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value };
      return newFilters;
    });
  };

  const handleDelete = (id: number, name: string) => {
    setSelectedHoliday({ id, name });
    toggleModal();
  };

  const confirmDelete = () => {
    if (selectedHoliday) {
      console.log(
        `Deleting holiday with ID: ${selectedHoliday.id}, Name: ${selectedHoliday.name}`
      );
      setModalOpen(false);
      setSelectedHoliday(null);
    }
  };

  const filteredItems = useMemo(() => {
    return holidayData.filter((item) => {
      const dateMatch =
        !filters.date ||
        (filters.date instanceof Date &&
          item.date ===
            filters.date.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }));
      return (
        item.name.toLowerCase().includes(filterText.toLowerCase()) &&
        (!filters.country || item.country === filters.country) &&
        (!filters.year || item.date.includes(filters.year)) &&
        dateMatch &&
        (!filters.type || item.type === filters.type) &&
        (!filters.status || item.status === filters.status)
      );
    });
  }, [filterText, filters]);

  const columns = useMemo(
    () => [
      {
        name: t("Sl.No"),
        selector: (row: { id: number }) => row.id,
        sortable: true,
        center: false,
        width: "100px",
      },
      {
        name: t("Country"),
        selector: (row: { country: string }) => row.country,
        sortable: true,
        center: false,
        width: "120px",
      },
      {
        name: t("HolidayName"),
        selector: (row: { name: string }) => row.name,
        sortable: true,
        center: false,
        wrap: true,
        width: "200px",
      },
      {
        name: t("Date"),
        selector: (row: { date: string }) => row.date,
        sortable: true,
        center: false,
      },
      {
        name: t("Type"),
        selector: (row: { type: string }) => row.type,
        sortable: true,
        center: false,
      },
      {
        name: t("Actions"),
        cell: (row: { id: number; name: string }) => (
          <UncontrolledDropdown>
            <DropdownToggle tag="span" className="cursor-pointer">
              <MoreVertical size={18} />
            </DropdownToggle>
            <DropdownMenu container="body">
              <Link to={`${process.env.PUBLIC_URL}/masterdata/new-holiday`}>
                <DropdownItem>{t("Edit")}</DropdownItem>
              </Link>
              <DropdownItem onClick={() => handleDelete(row.id, row.name)}>
                {t("Delete")}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
        center: true,
      },
    ],
    [t]
  );

  const searchBar = useMemo(
    () => (
      <div className="search-container">
        <Label className="search-label">{t("Search")}:</Label>
        <Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFilterText(e.target.value)
          }
          type="search"
          value={filterText}
          placeholder={t("SearchByHolidayName")}
          className="search-input"
        />
      </div>
    ),
    [filterText, t]
  );

  const filterSection = useMemo(
    () =>
      filterToggle ? (
        <div className="filter-section">
          <div className="filter-item">
            <Label>{t("Country")}</Label>
            <Input
              type="select"
              value={filters.country}
              onChange={(e) => handleFilterChange("country", e.target.value)}
            >
              {countries.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          </div>
          <div className="filter-item">
            <Label>{t("Year")}</Label>
            <Input
              type="select"
              value={filters.year}
              onChange={(e) => handleFilterChange("year", e.target.value)}
            >
              {years.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          </div>
          <div className="filter-item">
            <Label style={{ display: "block", marginBottom: "5px" }}>
              {t("Date")}
            </Label>
            <ReactDatePicker
              className="datepicker-here form-control"
              selected={filters.date}
              onChange={(date: Date | null) => handleFilterChange("date", date)}
              dateFormat="dd/MM/yyyy"
              placeholderText={t("All")}
              isClearable
            />
          </div>
          <div className="filter-item">
            <Label>{t("Type")}</Label>
            <Input
              type="select"
              value={filters.type}
              onChange={(e) => handleFilterChange("type", e.target.value)}
            >
              {types.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          </div>
        </div>
      ) : null,
    [filterToggle, filters, countries, years, dates, types, statuses, t]
  );

  const breadcrumbLinks = [
    { label: "Holidays", url: `${process.env.PUBLIC_URL}/masterdata/holidays` },
    ...(countryName
      ? [
          {
            label: t(decodeURIComponent(countryName)),
            url: `${
              process.env.PUBLIC_URL
            }/holidays/country/${encodeURIComponent(countryName)}`,
          },
        ]
      : []),
    ...(year ? [{ label: year, active: true }] : []),
    ...(!countryName && !year ? [{ label: "Holidays", active: true }] : []),
  ];

  return (
    <>
      <NavigationBreadcrumbs
        mainTitle={t("Holidays")}
        parent={t("Master Data")}
        links={breadcrumbLinks}
      />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Container fluid>
              <Row>
                <Col sm="12">
                  <Card>
                    <CardBody>
                      <div className="list-product-header">
                        <div className="header-container">
                          <div className="search-container">{searchBar}</div>
                          <div className="actions-container">
                            <div
                              className="light-box"
                              onClick={toggleFilterSection}
                              title={
                                filterToggle
                                  ? t("HideFilters")
                                  : t("ShowFilters")
                              }
                            >
                              {filterToggle ? (
                                <i className="icon-close" />
                              ) : (
                                <Filter />
                              )}
                            </div>
                            <Link
                              className="btn btn-primary action-btn"
                              to={`${process.env.PUBLIC_URL}/masterdata/new-holiday`}
                              title={t("AddHolidays")}
                            >
                              <i className="fa fa-plus" />
                              <span className="btn-text">
                                {t("AddHolidays")}
                              </span>
                            </Link>
                            <Link
                              className="btn btn-success action-btn"
                              to={`${process.env.PUBLIC_URL}/masterdata/import-holiday`}
                              title={t("Import")}
                            >
                              <i className="fa fa-download" />
                              <span className="btn-text">{t("Import")}</span>
                            </Link>

                            <div>
                              <CommonDropDown color="info" />
                            </div>
                          </div>
                        </div>
                        {filterSection}
                      </div>
                      <div className="list-product">
                        <div className="table-responsive">
                          <DataTable
                            data={filteredItems}
                            columns={columns}
                            striped
                            highlightOnHover
                            customStyles={tableHeaderStyles}
                            pagination
                          />
                        </div>
                      </div>
                      <Modal
                        fade
                        centered
                        isOpen={modalOpen}
                        toggle={toggleModal}
                      >
                        <div className="modal-dialog m-0">
                          <div className="modal-content">
                            <Col xl="12">
                              <Card className="social-profile mb-0">
                                <CardBody>
                                  <div className="social-details">
                                    <h5 style={{ marginBottom: "15px" }}>
                                      Are you sure you want to Delete
                                    </h5>
                                    <span
                                      style={{
                                        marginBottom: "20px",
                                        display: "block",
                                      }}
                                    >
                                      <H4>{selectedHoliday?.name || ""}?</H4>
                                    </span>
                                    <ModalFooter
                                      style={{ justifyContent: "center" }}
                                    >
                                      <Button
                                        color="danger"
                                        onClick={toggleModal}
                                      >
                                        {t("Cancel")}
                                      </Button>
                                      <Button
                                        color="success"
                                        onClick={confirmDelete}
                                      >
                                        {t("Confirm")}
                                      </Button>
                                    </ModalFooter>
                                  </div>
                                </CardBody>
                              </Card>
                            </Col>
                          </div>
                        </div>
                      </Modal>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Container>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default HolidaysTableList;
